import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { fetchProduct } from '../store/slices/productsSlice';

const ProductDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { currentProduct: product, loading } = useSelector((state) => state.products);

  useEffect(() => {
    dispatch(fetchProduct(id));
  }, [dispatch, id]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Laden...</span>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="alert alert-warning">
        Product niet gevonden
        <Link to="/products" className="btn btn-link">Terug naar producten</Link>
      </div>
    );
  }

  return (
    <div className="product-detail">
      <div className="page-header">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/products">Producten</Link></li>
            <li className="breadcrumb-item active">{product.name}</li>
          </ol>
        </nav>
        <h1>{product.name}</h1>
        <p className="text-muted"><code>{product.productCode}</code></p>
      </div>

      <div className="row">
        <div className="col-lg-8">
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Product Informatie</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="text-muted">Materiaal</label>
                  <p className="mb-0">
                    <span className="badge bg-primary">{product.materialType || '-'}</span>
                  </p>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted">Categorie</label>
                  <p className="mb-0 fw-medium">{product.category || '-'}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted">Rolbreedte</label>
                  <p className="mb-0 fw-medium">{product.rollWidth ? `${product.rollWidth} cm` : '-'}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted">Aanpasbaar</label>
                  <p className="mb-0">
                    {product.isCustomizable ? (
                      <span className="badge bg-success">Ja</span>
                    ) : (
                      <span className="badge bg-secondary">Nee</span>
                    )}
                  </p>
                </div>
              </div>
              {product.description && (
                <div className="mt-3">
                  <label className="text-muted">Beschrijving</label>
                  <p className="mb-0">{product.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Machine Settings */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Machine Instellingen</h5>
            </div>
            <div className="card-body p-0">
              {product.machineSettings?.length > 0 ? (
                <div className="table-responsive">
                  <table className="table mb-0">
                    <thead>
                      <tr>
                        <th>Machine</th>
                        <th>Snijsnelheid</th>
                        <th>Druk</th>
                        <th>Temperatuur</th>
                        <th>Tool</th>
                      </tr>
                    </thead>
                    <tbody>
                      {product.machineSettings.map((setting, index) => (
                        <tr key={index}>
                          <td>{setting.machine?.name || setting.machine}</td>
                          <td>{setting.cuttingSpeed ? `${setting.cuttingSpeed} cm/min` : '-'}</td>
                          <td>{setting.pressure || '-'}</td>
                          <td>{setting.temperature ? `${setting.temperature}°C` : '-'}</td>
                          <td>{setting.toolType || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  Geen machine instellingen geconfigureerd
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          {/* Stock Info */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="mb-0">Voorraad</h6>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Beschikbaar</span>
                <span className="fw-medium">{product.stock?.quantity || 0}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Gereserveerd</span>
                <span className="fw-medium">{product.stock?.reserved || 0}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted">Minimum voorraad</span>
                <span className="fw-medium">{product.stock?.minStock || 0}</span>
              </div>
            </div>
          </div>

          {/* Stock Locations */}
          {product.stockLocations?.length > 0 && (
            <div className="card mb-4">
              <div className="card-header">
                <h6 className="mb-0">Voorraadlocaties</h6>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Locatie</th>
                        <th className="text-end">Voorraad</th>
                        <th className="text-end">Vrij</th>
                      </tr>
                    </thead>
                    <tbody>
                      {product.stockLocations.map((loc, index) => (
                        <tr key={index}>
                          <td>
                            <span className="fw-medium">{loc.name}</span>
                            {loc.isBulkLocation && (
                              <span className="badge bg-info ms-2" style={{ fontSize: '0.65em' }}>Bulk</span>
                            )}
                          </td>
                          <td className="text-end">{loc.stock}</td>
                          <td className="text-end">{loc.freeStock}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Machines */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="mb-0">Gekoppelde Machines</h6>
            </div>
            <div className="card-body">
              {product.primaryMachine && (
                <div className="mb-3">
                  <span className="text-muted d-block small">Primaire machine</span>
                  <Link to={`/machines/${product.primaryMachine._id || product.primaryMachine}`}>
                    <span className="badge bg-primary">
                      {product.primaryMachine.name || product.primaryMachine}
                    </span>
                  </Link>
                </div>
              )}
              {product.compatibleMachines?.length > 0 && (
                <div>
                  <span className="text-muted d-block small mb-2">Compatibele machines</span>
                  <div className="d-flex flex-wrap gap-1">
                    {product.compatibleMachines.map((machine, index) => (
                      <Link
                        key={index}
                        to={`/machines/${machine._id || machine}`}
                      >
                        <span className="badge bg-secondary">
                          {machine.name || machine}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {!product.primaryMachine && !product.compatibleMachines?.length && (
                <p className="text-muted mb-0">Geen machines gekoppeld</p>
              )}
            </div>
          </div>

          {/* Productfields from Picqer */}
          {product.metadata?.picqerData?.productfields?.length > 0 && (
            <div className="card mb-4">
              <div className="card-header">
                <h6 className="mb-0">Picqer Productfields</h6>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Veld</th>
                        <th>Waarde</th>
                      </tr>
                    </thead>
                    <tbody>
                      {product.metadata.picqerData.productfields.map((field, index) => (
                        <tr key={index}>
                          <td>
                            <span className="text-muted">{field.title}</span>
                            <br />
                            <small className="text-muted">ID: {field.idproductfield}</small>
                          </td>
                          <td className="fw-medium">
                            {field.value || <span className="text-muted">-</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Metadata */}
          {product.metadata?.lastSyncedFromPicqer && (
            <div className="card">
              <div className="card-header">
                <h6 className="mb-0">Picqer Sync</h6>
              </div>
              <div className="card-body">
                <small className="text-muted">
                  Laatst gesynchroniseerd:{' '}
                  {new Date(product.metadata.lastSyncedFromPicqer).toLocaleString('nl-NL')}
                </small>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
